/** @jsx jsx */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
	type MouseEvent,
	type MouseEventHandler,
	type Ref,
	type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { styled, jsx, css } from '@compiled/react';
import throttle from 'lodash/throttle';
import noop from 'lodash/noop';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { Inline, xcss, media } from '@atlaskit/primitives';
import { token, type CSSToken } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import {
	COLUMN_ID_MERGED_COLUMN,
	MERGED_COLUMN_TYPES,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { FIELD } from '../../../../controllers/inline-create-active-index/types.tsx';
import {
	useInlineCreateActiveIndexActions,
	useInlineFieldCreateActiveIndex,
} from '../../../../controllers/inline-create-active-index/index.tsx';
import {
	COLUMN_OPERATION_REORDER,
	COLUMN_OPERATION_RESIZE,
	COLUMN_RESIZE_HANDLE_FULL_HEIGHT,
	MINIMUM_MERGED_CELL_COLUMN_WIDTH,
	SORT_ASC,
	SORT_DESC,
} from '../../../../common/constants.tsx';

import type {
	Column,
	ColumnId,
	TargetColumnPosition,
	ColumnResizeHandleType,
	BorderWidth,
	HeaderHeight,
	SortOrder,
} from '../../../../common/types.tsx';
import WarningIndicator from '../../../../common/ui/warning-indicator/index.tsx';
import {
	useIsColumnResizingEnabled,
	useIsInBetweenColumnPickerEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsMergedColumnEnabled,
	useIsChildIssuesTableVisualUpliftEnabled,
	useIsGridNavigationEnabled,
	useIsStickyColumnsEnabled,
	useIsAutoUnstickyOnSmallScreenEnabled,
	useIsInBetweenStickyColumnPickerEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { useScrollStateSelector } from '../../../../controllers/scroll-state/index.tsx';
import { getColumnOffset } from '../../utils.tsx';
import { HeaderCellActions } from './header-cell-actions/index.tsx';
import { ResizePopup } from './resize-popup/index.tsx';
import {
	clearCssResizingWidth,
	disableCssPointerEvents,
	enableCssPointerEvents,
	getProposedWidth,
	setCssResizingWidth,
	setCssResizingOffset,
	clearCssResizingOffset,
} from './utils.tsx';
import { ExpandAllButton } from './header-cell-actions/expand-all-button/index.tsx';

const DRAG_PREVIEW_MAX_WIDTH = 260;
const BORDER_WIDTH: BorderWidth = '1px';
const HEADER_HEIGHT: HeaderHeight = 40;
export const MERGED_COLUMN_WIDTH_SAFETY_OFFSET = 60;

// ensure that the resize handle displays above subsequent header cells, but that a sticky cell will display above the
// resize handle. Improper z-index may cause the resize handle to render _above_ the column picker.
const RESIZE_HANDLE_Z_INDEX = 1;
const STICKY_CELL_Z_INDEX = RESIZE_HANDLE_Z_INDEX + 1;
const BOUNDARY = 70;
const MOUSE_MOVE_THROTTLE = 16;

type Props = {
	column: Column;
	previousColumn?: Column;
	hasColumnConfiguration: boolean;
	instanceId: symbol;
	isCompactColumnEnabled?: boolean;
	isFirst: boolean;
	isLast: boolean;
	resizeHandleType?: ColumnResizeHandleType;
	shouldHaveResizeHandle?: boolean;
	tableRef?: RefObject<HTMLTableElement>;
	onMoveColumn: (position: TargetColumnPosition) => void;
	onRemoveColumn: () => void;
	onResizeColumn?: (columnId: ColumnId, width: number | undefined) => void;
	onFreezeColumn?: (updatedColumns: { fieldSetId: string; isFrozen: boolean }[]) => void;
	index: number;
	stickyColumnsWidth?: number;
	additionalStickyColumnsCount?: number;
	tableBorderRadius?: CSSToken;
	onDropdownStateChange?: (isOpen: boolean) => void;
	resizingColumnIndex?: number;
	setResizingColumnIndex?: (resizingColumnIndex: number | undefined) => void;
	enableDynamicOrderForMergedColumn?: boolean;
	isStickyColumnsVisible?: boolean;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	mergedColumnMinWidth?: number;
	totalMoveableColumns?: number;
};

type State =
	| { type: 'idle' }
	| { type: 'preview'; container: HTMLElement; previewWidth: number }
	| { type: 'reordering' }
	| { type: 'resizing' };

const SpotlightWrapper = ({ children }: { children: React.ReactNode }) => {
	return (
		<div
			css={spotlightWrapperStyles}
			data-testid="native-issue-table.ui.issue-table.header.header-cell.spotlight-wrapper"
		>
			{children}
		</div>
	);
};

export const HeaderCell = ({
	column,
	hasColumnConfiguration,
	instanceId,
	isCompactColumnEnabled = false,
	isFirst,
	isLast,
	previousColumn,
	resizeHandleType = COLUMN_RESIZE_HANDLE_FULL_HEIGHT,
	shouldHaveResizeHandle = true,
	tableRef,
	onMoveColumn: onMoveColumnProp,
	onRemoveColumn,
	onResizeColumn: onResizeColumnProp,
	onFreezeColumn: onFreezeColumnProp,
	index,
	tableBorderRadius,
	onDropdownStateChange,
	stickyColumnsWidth = 0,
	additionalStickyColumnsCount = 0,
	resizingColumnIndex,
	setResizingColumnIndex,
	enableDynamicOrderForMergedColumn,
	isStickyColumnsVisible,
	mergedColumnMinWidth,
	totalMoveableColumns,
}: Props) => {
	const headerCellContainerRef = useRef<HTMLTableCellElement>(null);
	const headerCellActionsTriggerRef = useRef<HTMLButtonElement>(null);
	const resizeHandleRef = useRef<HTMLDivElement>(null);

	const isColumnResizingEnabled = useIsColumnResizingEnabled();
	const isInBetweenColumnPickerEnabled = useIsInBetweenColumnPickerEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const isStickyColumnsEnabled = useIsStickyColumnsEnabled();
	const isInBetweenStickyColumnPickerEnabled = useIsInBetweenStickyColumnPickerEnabled();

	let isAutoUnstickyOnSmallScreenEnabled = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	}

	let isChildIssuesTableVisualUpliftEnabled = false;
	if (fg('fg_visual_uplift_for_child_issues_table')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isChildIssuesTableVisualUpliftEnabled = useIsChildIssuesTableVisualUpliftEnabled();
	}

	const [isHovered, setIsHovered] = useState(false);

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const handleDropdownOpenChange = useCallback(
		(isOpen: boolean) => {
			setIsDropdownOpen(isOpen);
			if (onDropdownStateChange) {
				onDropdownStateChange(isOpen);
			}
		},
		[onDropdownStateChange],
	);

	const [state, setState] = useState<State>({ type: 'idle' });

	const [dropEdge, setDropEdge] = useState<Edge | null>(null);

	let clearIndex = noop;
	let setIndex = noop;
	let clearIndexAfterDelay = noop;
	let columnIndex;
	let isInlineFieldCreateHovered;
	if (isInBetweenColumnPickerEnabled) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ clearIndex, setIndex, clearIndexAfterDelay } = useInlineCreateActiveIndexActions());
		[{ index: columnIndex, isHovered: isInlineFieldCreateHovered }] =
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useInlineFieldCreateActiveIndex();
	}
	const height = useScrollStateSelector((scrollState) => scrollState.height);

	let scrollElement: HTMLElement | null | undefined;
	let scrollLeft = 0;
	let isScrolling = false;
	let hasScrolled = false;
	let tableWidth = 0;
	if (fg('issue_table_frozen_columns')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const scrollState = useScrollStateSelector((s) => ({
			scrollLeft: s.scrollLeft,
			isScrolling: fg('jira_list_frozen_column_bugfix')
				? s.isScrolling && s.hasScrolled
				: s.isScrolling,
			width: fg('jira_list_frozen_column_bugfix') ? 0 : s.width,
			hasScrolled: s.hasScrolled,
			scrollElement: s.scrollElement,
		}));

		scrollLeft = scrollState.scrollLeft ?? 0;
		isScrolling = scrollState.isScrolling ?? false;
		hasScrolled = scrollState.hasScrolled ?? false;
		scrollElement = scrollState.scrollElement;
		tableWidth = scrollState.width ?? 0;
	}
	tableWidth = fg('jira_list_frozen_column_bugfix')
		? (scrollElement?.clientWidth ?? 0)
		: tableWidth;

	if (
		isStickyColumnsVisible &&
		isScrolling &&
		isDropdownOpen &&
		!column.isColumnSticky &&
		fg('issue_table_frozen_columns')
	) {
		const scrollElementToLeft = scrollElement?.getBoundingClientRect().left ?? 0;
		const triggerOffsetFromLeft = headerCellActionsTriggerRef.current?.getBoundingClientRect().left;
		if (
			triggerOffsetFromLeft !== undefined &&
			triggerOffsetFromLeft - scrollElementToLeft < stickyColumnsWidth
		) {
			setIsDropdownOpen(false);
		}
	}

	const analyticsAttributes = useMemo(
		() => ({
			fieldType: column.type,
		}),
		[column.type],
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isStickyColumn = useMemo(
		() =>
			column.isColumnSticky &&
			isInBetweenStickyColumnPickerEnabled &&
			fg('jira_timeline_sticky_assignee_status_columns'),
		[column.isColumnSticky, isInBetweenStickyColumnPickerEnabled],
	);

	const throttledHandler = useMemo(
		() =>
			throttle((event: MouseEvent) => {
				if (!event.currentTarget) {
					return;
				}

				const scrollElementToLeft = scrollElement?.getBoundingClientRect().left ?? 0;
				const stickyBoundary = scrollElementToLeft + stickyColumnsWidth;
				const rect = event.currentTarget.getBoundingClientRect();
				const x = event.clientX - rect.left;
				const width = rect.right - rect.left;

				// For sticky columns, allow insert button on both sides
				// For non-sticky columns, only allow on the right side if they're past the sticky boundary
				if (x < width / 2 && x <= BOUNDARY) {
					// Left side: allow for sticky columns, or non-sticky columns past the boundary
					if (isStickyColumn || rect.left >= stickyBoundary) {
						setIndex(FIELD, index - 1);
					} else {
						clearIndex(FIELD);
					}
				} else if (x >= width / 2 && x >= width - BOUNDARY) {
					// Right side: always allow
					setIndex(FIELD, index);
				} else {
					clearIndex(FIELD);
				}
			}, MOUSE_MOVE_THROTTLE),
		[scrollElement, stickyColumnsWidth, setIndex, index, clearIndex, isStickyColumn],
	);

	const calculateColumnIndex = useCallback(throttledHandler, [throttledHandler]);

	const onMouseEnter = useCallback((event: MouseEvent) => {
		if (event.target === resizeHandleRef.current) {
			// prevent showing "hovered" state when only the resize handle is hovered
			return;
		}
		setIsHovered(true);
	}, []);

	const onMouseLeave = useCallback(() => {
		setIsHovered(false);
		if (isInBetweenColumnPickerEnabled) {
			clearIndexAfterDelay(FIELD);
		}
	}, [clearIndexAfterDelay, isInBetweenColumnPickerEnabled]);

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			event.persist();

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (headerCellContainerRef.current?.contains(event.target as HTMLElement)) {
				calculateColumnIndex(event);
			} else {
				clearIndex(FIELD);
			}
		},
		[calculateColumnIndex, clearIndex],
	);

	const handleMouseClick = useCallback(
		(event: MouseEvent) => {
			if (
				event.target instanceof HTMLElement &&
				headerCellContainerRef.current?.contains(event.target.closest('button'))
			) {
				clearIndex(FIELD);
			}
		},
		[clearIndex],
	);

	// #region Column reordering
	const onMoveColumn = useCallback(
		(position: TargetColumnPosition) => {
			onMoveColumnProp(position);
			setIsHovered(false);
		},
		[onMoveColumnProp],
	);

	let isMergedColumnEnabled = false;
	if (fg('jira_list_merged_column_gate')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isMergedColumnEnabled = useIsMergedColumnEnabled();
	}

	const isMergedColumn =
		isMergedColumnEnabled &&
		column.id === COLUMN_ID_MERGED_COLUMN &&
		fg('jira_list_merged_column_gate');

	const isHiddenByFrozenColumn = useMemo(() => {
		if (
			isStickyColumnsVisible &&
			resizeHandleRef &&
			resizeHandleRef.current &&
			hasScrolled &&
			fg('issue_table_frozen_columns')
		) {
			if (isMergedColumn && fg('jira_list_frozen_column_bugfix')) {
				return false;
			}
			const scrollElementToLeft = scrollElement?.getBoundingClientRect().left ?? 0;
			const stickyBoundary = scrollElementToLeft + stickyColumnsWidth;
			return resizeHandleRef.current.getBoundingClientRect().left < stickyBoundary || isScrolling;
		}
		return false;
	}, [
		isMergedColumn,
		isStickyColumnsVisible,
		hasScrolled,
		scrollElement,
		stickyColumnsWidth,
		isScrolling,
	]);

	const isMultiColumnMergedCell =
		isMergedColumn && column.mergedColumns && column.mergedColumns.length > 1;

	useEffect(() => {
		const headerCellContainer = headerCellContainerRef.current;
		const allowMergedColumnReordering = fg('jira-linked-work-items-targeting-gate')
			? enableDynamicOrderForMergedColumn
			: false;

		if (
			!hasColumnConfiguration ||
			!headerCellContainer ||
			(isMergedColumn && !allowMergedColumnReordering)
		) {
			return;
		}

		return combine(
			draggable({
				element: headerCellContainer,
				getInitialData() {
					if (isStickyColumnsVisible) {
						return {
							operation: COLUMN_OPERATION_REORDER,
							id: column.id,
							instanceId,
							isColumnSticky: column.isColumnSticky,
						};
					}
					return { operation: COLUMN_OPERATION_REORDER, id: column.id, instanceId };
				},
				onDragStart() {
					setState({ type: 'reordering' });
				},
				onDrop() {
					setState({ type: 'idle' });
				},
				onGenerateDragPreview({ nativeSetDragImage }) {
					const headerCellWidth = headerCellContainer.getBoundingClientRect().width;
					setCustomNativeDragPreview({
						getOffset() {
							return { x: 20, y: 20 };
						},
						render({ container }) {
							// Cause a React re-render to create portal synchronously
							setState({
								type: 'preview',
								container,
								previewWidth: Math.min(headerCellWidth, DRAG_PREVIEW_MAX_WIDTH),
							});
							// Cause a React re-render to remove portal on cleanup
							return () => setState({ type: 'reordering' });
						},
						nativeSetDragImage,
					});
				},
			}),
			dropTargetForElements({
				element: headerCellContainer,
				canDrop({ source }) {
					if (isStickyColumnsVisible) {
						// Only allow drops from the same instance id
						// (i.e. prevent dropping entities that are not headers, or headers from another issue table)
						return (
							source.data.instanceId === instanceId &&
							// Only allow columns to be dropped within their original section:
							// frozen columns into the frozen section, and unfrozen columns into the unfrozen section
							column.isColumnSticky === source.data.isColumnSticky
						);
					}
					// Only allow drops from the same instance id
					// (i.e. prevent dropping entities that are not headers, or headers from another issue table)
					return source.data.instanceId === instanceId;
				},
				getData({ input, element }) {
					const data = { id: column.id, instanceId };
					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['left', 'right'],
					});
				},
				onDrag({ self }) {
					const closestEdge = extractClosestEdge(self.data);

					if (!closestEdge) {
						return;
					}

					setDropEdge(closestEdge);
				},
				onDragLeave() {
					setDropEdge(null);
				},
				onDrop() {
					setDropEdge(null);
				},
			}),
			monitorForElements({
				canMonitor({ source }) {
					// Only monitor this particular instance id
					// (i.e. prevent monitoring entities that are not headers, or headers from another issue table)
					return source.data.instanceId === instanceId;
				},
				onDrag() {
					// Close the dropdown menu when a drag starts on any column
					setIsDropdownOpen(false);
				},
			}),
		);
	}, [
		column.id,
		column.isColumnSticky,
		hasColumnConfiguration,
		instanceId,
		isMergedColumn,
		isStickyColumnsVisible,
		enableDynamicOrderForMergedColumn,
	]);
	// #endregion

	// #region Column resizing
	const mergedColumnWidth = Math.max(
		mergedColumnMinWidth || MINIMUM_MERGED_CELL_COLUMN_WIDTH,
		column.customWidth ?? column.defaultWidth,
	);

	const maxFrozenMergedColumnWidth =
		isMergedColumn &&
		column.isColumnSticky &&
		tableWidth > 0 &&
		(!fg('jira_timeline_sticky_assignee_status_columns') || additionalStickyColumnsCount === 0)
			? tableWidth - stickyColumnsWidth + mergedColumnWidth - MERGED_COLUMN_WIDTH_SAFETY_OFFSET
			: mergedColumnWidth;

	// eslint-disable-next-line no-nested-ternary
	const columnWidth = isMultiColumnMergedCell
		? fg('issue_table_frozen_columns')
			? Math.min(mergedColumnWidth, maxFrozenMergedColumnWidth)
			: mergedColumnWidth
		: (column.customWidth ?? column.defaultWidth);

	// eslint-disable-next-line no-nested-ternary
	const prevColumnCustomWidth = isMultiColumnMergedCell
		? fg('issue_table_frozen_columns')
			? Math.min(columnWidth, maxFrozenMergedColumnWidth)
			: columnWidth
		: column.customWidth;

	const [componentColumnWidth, setComponentColumnWidth] = useState(columnWidth);
	const prevColumnConfigCustomWidth = usePreviousWithInitial(prevColumnCustomWidth);
	const [isResizePopupOpen, setIsResizePopupOpen] = useState(false);

	const ariaSortDirection = (sortOrder?: SortOrder): 'ascending' | 'descending' | 'none' => {
		switch (sortOrder) {
			case SORT_ASC:
				return 'ascending';
			case SORT_DESC:
				return 'descending';
			default:
				return 'none';
		}
	};

	const onResizeColumn = useCallback(
		(width: number, resizeMethod: 'drag-and-drop' | 'popup' | 'reset') => {
			if (width === componentColumnWidth) {
				// Skip state updates if column width has not changed
				return;
			}

			setComponentColumnWidth(width);

			const resizeColumnId = column.resizeColumnId ?? column.id;

			onResizeColumnProp &&
				onResizeColumnProp(resizeColumnId, width === column.defaultWidth ? undefined : width);

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'resized',
					actionSubject: 'column',
				}),
				'issueTable',
				{
					...analyticsAttributes,
					resizeMethod,
					columnWidth: width,
				},
			);
		},
		[
			componentColumnWidth,
			onResizeColumnProp,
			column.id,
			column.defaultWidth,
			column.resizeColumnId,
			createAnalyticsEvent,
			analyticsAttributes,
		],
	);

	const onFreezeColumn = useCallback(() => {
		if (column.id === COLUMN_ID_MERGED_COLUMN) {
			onFreezeColumnProp?.(
				MERGED_COLUMN_TYPES.map((fieldSetId) => ({
					fieldSetId,
					isFrozen: true,
				})),
			);
		} else {
			onFreezeColumnProp?.([{ fieldSetId: column.id, isFrozen: true }]);
		}
	}, [column.id, onFreezeColumnProp]);

	const onUnfreezeColumn = useCallback(() => {
		if (column.id === COLUMN_ID_MERGED_COLUMN) {
			onFreezeColumnProp?.(
				MERGED_COLUMN_TYPES.map((fieldSetId) => ({
					fieldSetId,
					isFrozen: false,
				})),
			);
		} else {
			onFreezeColumnProp?.([{ fieldSetId: column.id, isFrozen: false }]);
		}
	}, [column.id, onFreezeColumnProp]);

	// Header actions callbacks
	const onResizeColumnAction = useCallback(() => {
		setIsResizePopupOpen(true);
		if (
			column.isColumnSticky &&
			additionalStickyColumnsCount > 0 &&
			fg('jira_timeline_sticky_assignee_status_columns')
		) {
			setResizingColumnIndex?.(index);
		}
	}, [column.isColumnSticky, setResizingColumnIndex, index, additionalStickyColumnsCount]);

	const onResetColumnWidthAction = useCallback(() => {
		onResizeColumn(column.defaultWidth, 'reset');
	}, [onResizeColumn, column.defaultWidth]);

	// Resize popup callbacks
	const onChangeResizePopup = useCallback(
		(width: number) => {
			setCssResizingWidth(headerCellContainerRef.current, width);

			// Update CSS offset for other sticky columns to reposition in real-time
			if (
				tableRef &&
				column.isColumnSticky &&
				additionalStickyColumnsCount > 0 &&
				fg('jira_timeline_sticky_assignee_status_columns')
			) {
				const diffWidth = width - componentColumnWidth;
				setCssResizingOffset(tableRef.current, diffWidth);
			}
		},
		[tableRef, column.isColumnSticky, componentColumnWidth, additionalStickyColumnsCount],
	);

	const onCloseResizePopup = useCallback(
		(event?: KeyboardEvent | MouseEvent, overrideKeyboardCheck?: boolean) => {
			setIsResizePopupOpen(false);
			clearCssResizingWidth(headerCellContainerRef.current);

			// Clear resizing state for sticky columns
			if (
				tableRef &&
				column.isColumnSticky &&
				additionalStickyColumnsCount > 0 &&
				fg('jira_timeline_sticky_assignee_status_columns')
			) {
				setResizingColumnIndex?.(undefined);
				clearCssResizingOffset(tableRef.current);
			}

			// Focus header cell actions trigger if the resize popup was closed by keyboard
			// navigation or an override was requested
			//
			// `event.detail` docs: https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
			// (click count is 0 when using keyboard to activate a button)
			if (overrideKeyboardCheck === true || (event && event.detail === 0)) {
				requestAnimationFrame(() => headerCellActionsTriggerRef.current?.focus());
			}
		},
		[tableRef, column.isColumnSticky, setResizingColumnIndex, additionalStickyColumnsCount],
	);

	const onSubmitResizePopup = useCallback(
		(width: number) => {
			onResizeColumn(width, 'popup');
			// No event available from form submission. We will re-focus the actions
			// trigger because submit can't be triggerd by clicking outside of popup,
			// so don't have to worry about stealing focus from what the user clicked
			onCloseResizePopup(undefined, true);
		},
		[onResizeColumn, onCloseResizePopup],
	);

	// Resize handle callbacks
	const onMouseEnterResizeHandle: MouseEventHandler = useCallback((event) => {
		// prevent showing the drag & drop/reorder hover effect when resizing
		setIsHovered(false);
		event.stopPropagation();
	}, []);

	const onMouseLeaveResizeHandle: MouseEventHandler = useCallback(
		(event) => {
			// restore header hover state if mouse exits into the header element
			if (
				state.type !== 'resizing' &&
				(event.relatedTarget === headerCellContainerRef.current ||
					(event.relatedTarget instanceof Node &&
						headerCellContainerRef.current?.contains(event.relatedTarget)))
			) {
				setIsHovered(true);
			}
		},
		[state.type],
	);

	// Update column width state if a change is made in the column config
	useEffect(() => {
		if (fg('restore-default-width-in-issue-table')) {
			// Consider if `customWidth` doesn't exist, e.g., restore default columns is triggered
			// In this case, use the `defaultWidth` instead
			const nextColumnWidth = isMultiColumnMergedCell
				? columnWidth
				: (column.customWidth ?? column.defaultWidth);

			if (
				prevColumnConfigCustomWidth !== prevColumnCustomWidth &&
				componentColumnWidth !== prevColumnCustomWidth &&
				nextColumnWidth != null
			) {
				// Update merged cell width state if a change is made in the column config
				if (isMultiColumnMergedCell) {
					setComponentColumnWidth(columnWidth);
				} else {
					setComponentColumnWidth(nextColumnWidth);
				}
			}
		} else if (
			prevColumnConfigCustomWidth !== column.customWidth &&
			componentColumnWidth !== column.customWidth &&
			column.customWidth != null
		) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setComponentColumnWidth(column.customWidth);
		}
	}, [
		column.customWidth,
		column.defaultWidth,
		prevColumnCustomWidth,
		componentColumnWidth,
		isMultiColumnMergedCell,
		columnWidth,
		prevColumnConfigCustomWidth,
	]);

	const setFocusNone = () => {
		/*
			If drag ends, setFocusNone.
			if drag never starts but mouse down then back up, setFocusNone.
			Focus should be yielded back when dragging ends.
		*/
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const focused = document.activeElement;
		if (!(focused instanceof HTMLElement)) return;
		focused.blur();
	};

	// Creates draggable resize handle
	useEffect(() => {
		const resizeHandle = resizeHandleRef.current;
		if (!resizeHandle || isResizePopupOpen) {
			return;
		}

		return draggable({
			element: resizeHandle,
			getInitialData() {
				return { operation: COLUMN_OPERATION_RESIZE, id: column.id };
			},
			onGenerateDragPreview: ({ nativeSetDragImage }) => {
				// we will be moving the line to indicate a drag
				// we can disable the native drag preview
				disableNativeDragPreview({ nativeSetDragImage });
				// we don't want any native drop animation for when the user
				// does not drop on a drop target. we want the drag to finish immediately
				preventUnhandled.start();
			},
			onDragStart() {
				setState({ type: 'resizing' });
				if (
					column.isColumnSticky &&
					!fg('jira_timeline_sticky_assignee_status_columns') &&
					fg('issue_table_frozen_columns')
				) {
					setResizingColumnIndex?.(index);
				}
				// Disable pointer events on the table while dragging in order to prevent buggy hover states in chrome
				// https://issues.chromium.org/issues/41129937#comment43
				disableCssPointerEvents(tableRef?.current);
			},
			onDrag({ location }) {
				//  stickyColumnsWidth - componentColumnWidth + proposedWidth  < tableWidth
				const proposedWidth =
					isMergedColumn && column.isColumnSticky && fg('issue_table_frozen_columns')
						? Math.min(
								getProposedWidth({
									initialWidth: componentColumnWidth,
									location,
									isCompactColumnEnabled,
									isSingleLineRowHeightEnabled,
									isMultiColumnMergedCell,
									mergedColumnMinWidth,
								}),
								tableWidth -
									stickyColumnsWidth +
									componentColumnWidth -
									MERGED_COLUMN_WIDTH_SAFETY_OFFSET,
							)
						: getProposedWidth({
								initialWidth: componentColumnWidth,
								location,
								isCompactColumnEnabled,
								isSingleLineRowHeightEnabled,
								isMultiColumnMergedCell,
								mergedColumnMinWidth,
							});

				if (tableRef && column.isColumnSticky && fg('issue_table_frozen_columns')) {
					const diffX = location.current.input.clientX - location.initial.input.clientX;
					// prevents the offset from being set when the width is already at the maximum width
					if (
						additionalStickyColumnsCount > 0 &&
						fg('jira_timeline_sticky_assignee_status_columns')
					) {
						setCssResizingOffset(tableRef.current, proposedWidth - componentColumnWidth);
					} else {
						setCssResizingOffset(
							tableRef.current,
							Math.max(proposedWidth - componentColumnWidth, diffX),
						);
					}
				}

				setCssResizingWidth(headerCellContainerRef.current, proposedWidth);
			},
			onDrop({ location }) {
				const proposedWidth = getProposedWidth({
					initialWidth: componentColumnWidth,
					location,
					isCompactColumnEnabled,
					isSingleLineRowHeightEnabled,
					isMultiColumnMergedCell,
					mergedColumnMinWidth,
				});
				preventUnhandled.stop();
				setState({ type: 'idle' });
				if (tableRef && column.isColumnSticky && fg('issue_table_frozen_columns')) {
					setResizingColumnIndex?.(undefined);
					clearCssResizingOffset(tableRef.current);
				}
				onResizeColumn(
					isMergedColumn && column.isColumnSticky && fg('issue_table_frozen_columns')
						? Math.min(
								proposedWidth,
								tableWidth -
									stickyColumnsWidth +
									componentColumnWidth -
									MERGED_COLUMN_WIDTH_SAFETY_OFFSET,
							)
						: proposedWidth,
					'drag-and-drop',
				);

				// Re-enable pointer events to restore normal behaviour
				// https://issues.chromium.org/issues/41129937#comment43
				enableCssPointerEvents(tableRef?.current);
				clearCssResizingWidth(headerCellContainerRef.current);
				setFocusNone();
			},
		});
	}, [
		isResizePopupOpen,
		column.id,
		componentColumnWidth,
		tableRef,
		onResizeColumn,
		isCompactColumnEnabled,
		column.type,
		isSingleLineRowHeightEnabled,
		index,
		column.isColumnSticky,
		setResizingColumnIndex,
		isMultiColumnMergedCell,
		stickyColumnsWidth,
		isMergedColumn,
		mergedColumnMinWidth,
		tableWidth,
		additionalStickyColumnsCount,
	]);

	const MaybeSpotlightWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => {
			if (isMergedColumn && isStickyColumnsEnabled && fg('frozen_columns_onboarding')) {
				return <SpotlightWrapper>{children}</SpotlightWrapper>;
			}
			return <>{children}</>;
		},
		[isMergedColumn, isStickyColumnsEnabled],
	);

	// #endregion

	// #region Render
	const headerCell = (
		<ContextualAnalyticsData attributes={analyticsAttributes}>
			<HeaderCellContainer
				ref={headerCellContainerRef}
				scope="col"
				columnWidth={componentColumnWidth}
				minColumnWidth={!isColumnResizingEnabled ? column.customWidth : undefined}
				isDraggable={
					isMergedColumn
						? ((enableDynamicOrderForMergedColumn && fg('jira-linked-work-items-targeting-gate')) ??
							false)
						: hasColumnConfiguration
				}
				isHovered={isHovered}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				isFirst={isFirst}
				isLast={isLast}
				index={index}
				{...(isInBetweenColumnPickerEnabled &&
					((isInBetweenStickyColumnPickerEnabled &&
						fg('jira_timeline_sticky_assignee_status_columns')) ||
						!(column.isColumnSticky && fg('issue_table_frozen_columns'))) && {
						onMouseMove: handleMouseMove,
					})}
				{...(fg('jira_inline_field_config_gate') && { onClick: handleMouseClick })}
				// aria-label is added to prevent the extra accessibility hints in the header cell from being
				// read as the column title when a user is navigating between columns in the table content.
				aria-label={column.title}
				visualRefresh={isVisualRefreshEnabled()}
				isCompactColumnEnabled={isCompactColumnEnabled}
				tableBorderRadius={tableBorderRadius}
				tabIndex={index === 0 ? 0 : -1}
				isResizing={state.type === 'resizing'}
				{...(isStickyColumnsVisible &&
					fg('issue_table_frozen_columns') && {
						isColumnSticky: column.isColumnSticky,
						stickyOffsets: column.stickyOffsets,
						hasRightShadow: column.isLastStickyColumn && scrollLeft > 0,
						isColumnResizing: column.isColumnSticky && (resizingColumnIndex ?? 0) < index,
					})}
				{...(isStickyColumnsVisible &&
					fg('jira_list_frozen_column_bugfix') && {
						isAutoUnstickyOnSmallScreenEnabled,
					})}
				aria-sort={ariaSortDirection(column.currentSorting)}
				isChildIssuesTableVisualUpliftEnabled={isChildIssuesTableVisualUpliftEnabled}
				isGridNavigationEnabled={isGridNavigationEnabled}
				isFrozenColumnsSpotlightVisible={
					isMergedColumn && isStickyColumnsEnabled && fg('frozen_columns_onboarding')
				}
			>
				<MaybeSpotlightWrapper>
					<Inline
						alignBlock="center"
						spread="space-between"
						xcss={[
							state.type === 'reordering' && reorderingStyles,
							isMergedColumn &&
								isStickyColumnsEnabled &&
								fg('frozen_columns_onboarding') &&
								spotlightInnerStyles,
						]}
						testId="native-issue-table.ui.issue-table.header.header-cell.header-cell-container-inline"
					>
						<Inline alignBlock="center" space="space.050" xcss={overflowHiddenStyles}>
							{isMergedColumn && fg('jsc_list_expand_all') && <ExpandAllButton />}
							{column.warningText && <WarningIndicator tooltip={column.warningText} />}
							{fg('jira_list_merged_column_gate') && column.icon}
							<Title>{column.title}</Title>
						</Inline>
						<HeaderCellActions
							column={column}
							previousColumn={previousColumn}
							currentColumnWidth={componentColumnWidth}
							hasColumnConfiguration={
								isMergedColumn
									? ((enableDynamicOrderForMergedColumn &&
											fg('jira-linked-work-items-targeting-gate')) ??
										false)
									: hasColumnConfiguration
							}
							isColumnResizingEnabled={isColumnResizingEnabled}
							isCompactColumnEnabled={isCompactColumnEnabled}
							isHeaderCellHovered={isHovered}
							isFirst={isFirst}
							isLast={isLast}
							isOpen={isDropdownOpen}
							triggerRef={headerCellActionsTriggerRef}
							isMergedColumn={isMergedColumn}
							enableDynamicOrderForMergedColumn={enableDynamicOrderForMergedColumn}
							onMoveColumn={onMoveColumn}
							onOpenChange={handleDropdownOpenChange}
							onRemoveColumn={onRemoveColumn}
							onResizeColumn={onResizeColumnAction}
							onResetColumnWidth={onResetColumnWidthAction}
							onFreezeColumn={onFreezeColumnProp ? onFreezeColumn : undefined}
							onUnfreezeColumn={onFreezeColumnProp ? onUnfreezeColumn : undefined}
							mergedColumnMinWidth={mergedColumnMinWidth}
							{...(fg('jira_timeline_sticky_assignee_status_columns') && {
								totalMoveableColumns,
							})}
						/>
					</Inline>
				</MaybeSpotlightWrapper>
				{isColumnResizingEnabled && (
					<>
						<ResizePopup
							columnTitle={column.title}
							initialWidth={componentColumnWidth}
							placement="bottom-start"
							isOpen={isResizePopupOpen}
							isCompactColumnEnabled={isCompactColumnEnabled}
							isSingleLineRowHeightEnabled={isSingleLineRowHeightEnabled}
							trigger={resizePopupTarget}
							onChange={onChangeResizePopup}
							onCancel={onCloseResizePopup}
							onSubmit={onSubmitResizePopup}
							isMultiColumnMergedCell={isMultiColumnMergedCell}
							{...(fg('jira_timeline_sticky_assignee_status_columns') && {
								mergedColumnMinWidth,
							})}
						/>
						{!isResizePopupOpen &&
							fg('use_native_css_to_set_column_resize_handle_height') &&
							(() => {
								const commonProps = {
									ref: resizeHandleRef,
									isActive:
										state.type === 'resizing' ||
										(columnIndex === index &&
											isInlineFieldCreateHovered &&
											fg('jira_inline_field_config_gate')),
									...(fg('issue_table_frozen_columns') && {
										isHiddenByFrozenColumn,
									}),
									onMouseEnter: onMouseEnterResizeHandle,
									onMouseLeave: onMouseLeaveResizeHandle,
									onDoubleClick: onResetColumnWidthAction,
									onMouseDown: () => {
										if (
											column.isColumnSticky &&
											fg('jira_timeline_sticky_assignee_status_columns')
										) {
											setResizingColumnIndex?.(index);
										}
										setState({ type: 'resizing' });
									},
									onMouseUp: () => {
										if (
											column.isColumnSticky &&
											fg('jira_timeline_sticky_assignee_status_columns')
										) {
											setResizingColumnIndex?.(undefined);
										}
										setFocusNone();
										setState({ type: 'idle' });
									},
									'data-testid':
										'native-issue-table.ui.issue-table.header.header-cell.resize-handle',
									'data-vc': 'native-issue-table-ui-resize-handle',
									...(isInBetweenColumnPickerEnabled && {
										onMouseMove: (e: React.MouseEvent) => e.stopPropagation(),
									}),
								};
								if (!shouldHaveResizeHandle) {
									return;
								}

								return resizeHandleType === COLUMN_RESIZE_HANDLE_FULL_HEIGHT ? (
									<ResizeHandleFullHeight {...commonProps} />
								) : (
									<ResizeHandleHeaderOnly {...commonProps} />
								);
							})()}
						{!isResizePopupOpen &&
							shouldHaveResizeHandle &&
							!fg('use_native_css_to_set_column_resize_handle_height') && (
								<ResizeHandleOld
									ref={resizeHandleRef}
									height={
										resizeHandleType === COLUMN_RESIZE_HANDLE_FULL_HEIGHT ? height : undefined
									}
									isActive={
										state.type === 'resizing' ||
										(columnIndex === index &&
											isInlineFieldCreateHovered &&
											fg('jira_inline_field_config_gate'))
									}
									{...(fg('issue_table_frozen_columns') && {
										isHiddenByFrozenColumn,
									})}
									onMouseEnter={onMouseEnterResizeHandle}
									onMouseLeave={onMouseLeaveResizeHandle}
									onMouseDown={() => {
										if (
											column.isColumnSticky &&
											fg('jira_timeline_sticky_assignee_status_columns')
										) {
											setResizingColumnIndex?.(index);
										}
										setState({ type: 'resizing' });
									}}
									onMouseUp={() => {
										if (
											column.isColumnSticky &&
											fg('jira_timeline_sticky_assignee_status_columns')
										) {
											setResizingColumnIndex?.(undefined);
										}
										setFocusNone();
										setState({ type: 'idle' });
									}}
									onDoubleClick={onResetColumnWidthAction}
									data-testid="native-issue-table.ui.issue-table.header.header-cell.resize-handle"
									data-vc="native-issue-table-ui-resize-handle"
									/*
									 * Consumers may currently update non-visual styles dynamically based off height prop.
									 * Here we allow consumers to exclude the update of height styles from VC calculation.
									 * Learn more about why this has been introduced https://jplat.atlassian.net/browse/EM-9208
									 */
									data-vc-nvs={fg('ignore_column_resize_handle')}
									{...(isInBetweenColumnPickerEnabled && {
										onMouseMove: (e) => e.stopPropagation(),
									})}
								/>
							)}
					</>
				)}
				{dropEdge && (
					<DropTarget
						data-testid="native-issue-table.ui.issue-table.header.header-cell.drop-target"
						height={height}
					>
						<DropIndicator edge={dropEdge} gap={BORDER_WIDTH} type="no-terminal" />
					</DropTarget>
				)}
				{state.type === 'preview' &&
					createPortal(
						<DragPreview
							data-testid="native-issue-table.ui.issue-table.header.header-cell.drag-preview"
							height={height}
							width={state.previewWidth}
						/>,
						state.container,
					)}
			</HeaderCellContainer>
		</ContextualAnalyticsData>
	);

	if (isMergedColumn && isStickyColumnsEnabled && fg('frozen_columns_onboarding')) {
		return (
			<SpotlightTarget name="frozen-columns-onboarding-spotlight">{headerCell}</SpotlightTarget>
		);
	}

	return headerCell;

	// #endregion
};

// #region Styles

/**
 * Invisible element to attach resizing popup to - the intermediate component is required to allow
 * passing the ref while blocking the other props from being passed to the underlying div.
 *
 * This is because the other props are intended for a trigger button, but we are only using this
 * for positioning pourposes.
 */
const resizePopupTarget = ({ ref }: { ref: Ref<HTMLDivElement> }) => (
	<ResizePopupTargetDiv ref={ref} />
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ResizePopupTargetDiv = styled.div({
	width: 0,
	height: 0,
	position: 'absolute',
	left: 0,
	bottom: `calc(${token('space.100')} - 1px)`, // extra 1px adjustment to account for cell border
});

const reorderingStyles = xcss({
	opacity: 0.4,
});

// Add overflow: hidden to the inline div so that the title can collapse its width if needed
// and give space to the right-hand side icons (sort/actions)
const overflowHiddenStyles = xcss({
	overflow: 'hidden',
});

const baseHeaderContainerCellStyles = {
	backgroundColor: token('elevation.surface.sunken'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderTopWidth: 0,
	borderBottomWidth: BORDER_WIDTH,
	borderLeftWidth: 0,
	borderRightWidth: BORDER_WIDTH,
	'&:first-of-type': {
		borderTopLeftRadius: token('radius.large'),
	},
	'&:last-of-type': {
		borderTopRightRadius: token('radius.large'),
		borderRightWidth: 0,
	},
} as const;

const baseHeaderContainerCellKeyboardStyles = {
	backgroundColor: token('elevation.surface.sunken'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderTopWidth: 0,
	borderBottomWidth: BORDER_WIDTH,
	borderLeftWidth: 0,
	borderRightWidth: BORDER_WIDTH,
	'&:focus': {
		outline: 'unset',
		borderRight: `2px solid ${token('color.border.focused')}`,
		boxShadow: `inset 0 2px 0 0 ${token('color.border.focused')}`,
		'&:before': {
			content: ' ',
			display: 'block',
			position: 'absolute',
			height: '100%',
			width: '100%',
			top: '-3px',
			left: '-1px',
			right: '-1px',
			bottom: '-1px',
			border: `2px solid ${token('color.border.focused')}`,
		},
	},
} as const;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles
export const HeaderCellContainer = styled.th<{
	columnWidth?: number;
	minColumnWidth?: number;
	isDraggable?: boolean;
	isHovered?: boolean;
	isSticky?: boolean;
	hasShadow?: boolean;
	visualRefresh: boolean;
	isCompactColumnEnabled: boolean;
	tableBorderRadius?: CSSToken;
	isAutoUnstickyOnSmallScreenEnabled?: boolean;
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	isColumnResizing?: boolean;
	hasRightShadow?: boolean;
	isFirst?: boolean;
	isLast?: boolean;
	isResizing?: boolean;
	index?: number;
	isChildIssuesTableVisualUpliftEnabled: boolean;
	isGridNavigationEnabled: boolean;
	isFrozenColumnsSpotlightVisible?: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isGridNavigationEnabled }) =>
		isGridNavigationEnabled
			? {
					...baseHeaderContainerCellKeyboardStyles,
				}
			: {
					...baseHeaderContainerCellStyles,
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isResizing, isGridNavigationEnabled }) =>
		isResizing &&
		isGridNavigationEnabled && {
			'&:focus': {
				boxShadow: 'unset',
				borderRight: 'unset',
				'&:before': {
					border: 'unset',
				},
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isFirst, isGridNavigationEnabled }) =>
		isFirst &&
		isGridNavigationEnabled && {
			borderTopLeftRadius: token('radius.large'),
			'&:before': {
				left: 0,
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ index, isGridNavigationEnabled }) =>
		index === 2 &&
		isGridNavigationEnabled && {
			':focus::before': {
				top: '0px',
				right: '-1px',
				left: '-1px',
				bottom: '-1px',
				height: 'calc(100% - 2px)',
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ tableBorderRadius }) => ({
		position: 'relative',
		boxSizing: 'border-box',
		height: `${HEADER_HEIGHT}px`,
		maxHeight: `${HEADER_HEIGHT}px`,
		minHeight: `${HEADER_HEIGHT}px`,
		paddingBlock: token('space.0'),
		paddingInline: token('space.100'),
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopLeftRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingLeft: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:last-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopRightRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingRight: token('space.100'),
		},
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ columnWidth, minColumnWidth, isCompactColumnEnabled }) => ({
		// Width allows overrides when table is in fixed layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-width': columnWidth != null ? `${columnWidth}px` : 'auto',

		width: 'var(--local-resizing-width, var(--local-column-width))',
		// Min-width allows overrides when table is in auto layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-min-width': minColumnWidth != null ? `${minColumnWidth}px` : 'auto',
		minWidth: 'var(--local-resizing-width, var(--local-column-min-width))',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:first-of-type': {
			paddingLeft:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:last-of-type': {
			paddingRight:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
	}),
	// Programmatic `isHovered` state is used here so hover state isn't displayed when hovering the resize handle
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDraggable, isHovered }) =>
		isDraggable &&
		isHovered && {
			':hover': {
				cursor: 'grab',
				backgroundColor: token('elevation.surface.hovered'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({
		isSticky,
		isColumnSticky,
		stickyOffsets,
		isColumnResizing,
		hasRightShadow,
		isAutoUnstickyOnSmallScreenEnabled,
	}) =>
		(isSticky || isColumnSticky) && {
			position: 'sticky',
			zIndex: STICKY_CELL_Z_INDEX,
			...(isSticky && { right: 0 }),
			...(isColumnSticky && {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				left: getColumnOffset(stickyOffsets ?? 0, !!isColumnResizing),
			}),
			...(isAutoUnstickyOnSmallScreenEnabled &&
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				fg('jira_list_frozen_column_bugfix') && {
					[media.below.md]: {
						...(isColumnSticky && {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
							position: 'relative !important',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
							left: '0 !important',
						}),
					},
				}),
			...(hasRightShadow &&
				isAutoUnstickyOnSmallScreenEnabled &&
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				fg('jira_list_frozen_column_bugfix') && {
					[media.below.md]: {
						'&::after': {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
							width: '0 !important',
						},
					},
				}),
			...(hasRightShadow && {
				'&::after': {
					height: '100%',
					width: '4px',
					content: "''",
					opacity: 0.5,
					transition: 'opacity 0.25s',
					backgroundImage: 'linear-gradient(90deg, #0000001a 0%, #0000)',
					position: 'absolute',
					right: '-5px',
					top: token('space.0'),
				},
			}),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasShadow }) =>
		hasShadow && {
			boxShadow: `
                -8px 0 8px -8px ${token('elevation.shadow.overflow.spread')},
                -1px 0 1px -1px ${token('elevation.shadow.overflow.perimeter')}
            `,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			borderLeftWidth: `${BORDER_WIDTH} !important`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh }) =>
		visualRefresh
			? { color: token('color.text.subtle'), font: token('font.heading.xxsmall') }
			: {
					color: token('color.text.accent.gray'),
					fontWeight: token('font.weight.medium'),
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isChildIssuesTableVisualUpliftEnabled }) =>
		isChildIssuesTableVisualUpliftEnabled && {
			backgroundColor: 'none',
			borderRightWidth: '0',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isFrozenColumnsSpotlightVisible }) =>
		isFrozenColumnsSpotlightVisible && {
			paddingInline: '0',
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	paddingRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResizeHandleFullHeight = styled.div<{
	isActive?: boolean;
	isHiddenByFrozenColumn?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isActive }) => (isActive ? token('color.border.selected') : 'transparent'),
	backgroundClip: 'content-box',
	width: '2px',
	paddingInline: token('space.050'),
	position: 'absolute',
	top: token('space.0'),
	right: `calc(${token('space.negative.050')} - 1px)`, // extra 1px adjustment to account for normal cell borders
	// height will always match viewport height, and overflow will be hidden by scroll container
	height: '100vh',
	cursor: 'ew-resize',
	// ensure it renders above the following header cell, but not any dialogs/modals/etc
	zIndex: RESIZE_HANDLE_Z_INDEX,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isActive, isHiddenByFrozenColumn }) =>
			!isActive && !isHiddenByFrozenColumn ? token('color.border.bold') : undefined,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResizeHandleHeaderOnly = styled.div<{
	isActive?: boolean;
	isHiddenByFrozenColumn?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isActive }) => (isActive ? token('color.border.selected') : 'transparent'),
	backgroundClip: 'content-box',
	width: '2px',
	paddingInline: token('space.050'),
	position: 'absolute',
	top: token('space.0'),
	right: `calc(${token('space.negative.050')} - 1px)`, // extra 1px adjustment to account for normal cell borders
	// height will match header cell height
	height: '100%',
	cursor: 'ew-resize',
	// ensure it renders above the following header cell, but not any dialogs/modals/etc
	zIndex: RESIZE_HANDLE_Z_INDEX,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isActive, isHiddenByFrozenColumn }) =>
			!isActive && !isHiddenByFrozenColumn ? token('color.border.bold') : undefined,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResizeHandleOld = styled.div<{
	height?: number;
	isActive?: boolean;
	isHiddenByFrozenColumn?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isActive }) => (isActive ? token('color.border.selected') : 'transparent'),
	backgroundClip: 'content-box',
	width: '2px',
	paddingInline: token('space.050'),
	position: 'absolute',
	top: token('space.0'),
	right: `calc(${token('space.negative.050')} - 1px)`, // extra 1px adjustment to account for normal cell borders
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => (height != null ? `${height}px` : '100%'),
	cursor: 'ew-resize',
	// ensure it renders above the following header cell, but not any dialogs/modals/etc
	zIndex: RESIZE_HANDLE_Z_INDEX,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isActive, isHiddenByFrozenColumn }) =>
			!isActive && !isHiddenByFrozenColumn ? token('color.border.bold') : undefined,
	},
});

// Invisible box surrounding the full column for the `DropIndicator` to attach to
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropTarget = styled.div<{ height: number }>({
	position: 'absolute',
	top: token('space.0'),
	left: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	width: '100%',
});

// Custom drag preview that looks like a ghost blue column
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragPreview = styled.div<{ height: number; width: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => `${height}px`,
	maxHeight: '100vh',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}px`,
	borderColor: token('color.border.focused'),
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
	borderWidth: token('border.width.focused'),
	backgroundColor: token('color.blanket.selected'),
});

const spotlightWrapperStyles = css({
	backgroundColor: token('elevation.surface.sunken'),
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	color: token('color.text.subtle'),
	font: token('font.heading.xxsmall'),
	paddingInline: token('space.100'),
	borderRadius: token('radius.xsmall'),
	width: `calc(100% - ${token('space.200')})`,
});

const spotlightInnerStyles = xcss({
	width: '100%',
});
